<template>
	<div class="sgnl-flex sgnl-flex-column sgnl-gap-16">
		<!-- User has an organization -->
		<template v-if="hasOrganization">
			<div class="tabs-container">
				<v-tabs
					v-model="currentTab"
					height="auto"
				>
					<v-tab :value="0">
						<span>Members</span>
					</v-tab>
					<v-tab :value="1">
						<span>Usage</span>
					</v-tab>
					<v-tab :value="2">
						<span>Settings</span>
					</v-tab>
				</v-tabs>
			</div>

			<v-window v-model="currentTab">
				<!-- Members Tab -->
				<v-window-item :value="0">
					<div class="content-sections">
						<section class="stack">
							<div
								class="members-header sgnl-flex sgnl-flex-column sgnl-flex-md-row sgnl-justify-space-between sgnl-align-start sgnl-align-md-center sgnl-gap-8"
							>
								<h4 class="sgnl-h6-semibold sgnl-text-color-default">Members</h4>
								<SkeletonBlock
									v-if="!roleResolved"
									btn
									width="166px"
								/>
								<v-btn
									v-else-if="isOwner"
									variant="flat"
									color="primary"
									@click="showInviteDialog = true"
									data-test="invite-member-button"
								>
									<Add16 />
									Invite Members
								</v-btn>
							</div>
							<ListTeamMembers
								ref="listTeamMembersComponent"
								@roleUpdated="handleRoleUpdated"
							/>
						</section>
					</div>
				</v-window-item>

				<!-- Usage Tab -->
				<v-window-item :value="1">
					<div class="content-sections">
						<section>
							<OrganizationResourceUsage
								:resourceUsage="organizationDetails?.ResourceUsage ?? null"
								:error="resourceUsageError"
							/>
						</section>
					</div>
				</v-window-item>

				<!-- Settings Tab -->
				<v-window-item :value="2">
					<div class="content-sections">
						<section class="stack">
							<h4 class="sgnl-h6-semibold sgnl-text-color-default">Organization</h4>
							<div class="org-settings-form stack">
								<div class="input-group">
									<div class="field-label">Organization Name</div>
									<v-text-field
										:model-value="organizationDetails?.Name"
										disabled
										hide-details
									/>
								</div>
								<div class="input-group">
									<div class="field-label">Organization ID</div>
									<v-text-field
										:model-value="organizationDetails?.OrganizationID"
										disabled
										hide-details
									/>
								</div>
								<div class="input-group">
									<div class="field-label">Tier</div>
									<v-text-field
										:model-value="organizationDetails?.Tier"
										disabled
										hide-details
									/>
								</div>
							</div>
						</section>

						<section
							v-if="isOwner"
							class="stack"
						>
							<h4 class="sgnl-h6-semibold sgnl-text-color-default">Delete Organization</h4>
							<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
								Permanently delete this organization and remove all members. This action cannot be
								undone.
							</div>
							<v-btn
								variant="outlined"
								color="error"
								disabled
							>
								Delete Organization
							</v-btn>
							<div class="sgnl-caption-regular sgnl-text-color-tertiary">
								Coming soon.
								<a
									href="mailto:developer-support@signaloid.com"
									class="support-link sgnl-text-color-brand"
									>Contact support</a
								>
								to delete your organization.
							</div>
						</section>
					</div>
				</v-window-item>
			</v-window>
		</template>

		<!-- User is Enterprise tier but doesn't have an organization -->
		<div
			v-else-if="isEnterpriseTier && !hasOrganization"
			class="content-sections"
		>
			<section class="stack">
				<h4 class="sgnl-h6-semibold sgnl-text-color-default">Create Your Organization</h4>
				<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
					As an Enterprise tier user, you can create an organization to manage your team, share resources, and
					collaborate on projects.
				</div>

				<v-form
					ref="createOrgForm"
					v-model="createOrgFormValid"
					class="stack"
					@submit.prevent="handleCreateOrganization"
				>
					<div class="sgnl-flex sgnl-flex-column sgnl-gap-8">
						<div class="sgnl-paragraph-m-medium sgnl-text-color-default">
							Benefits of creating an organization:
						</div>
						<ul class="ul-list sgnl-paragraph-s-regular sgnl-text-color-secondary">
							<li><DotMark16 class="ul-icon" />Centralized team management</li>
							<li><DotMark16 class="ul-icon" />Role-based access control (Owners and Members)</li>
							<li><DotMark16 class="ul-icon" />Shared resource pools across your team</li>
							<li><DotMark16 class="ul-icon" />Enhanced collaboration features</li>
							<li><DotMark16 class="ul-icon" />Organization-level resource usage tracking</li>
						</ul>
					</div>

					<div class="create-org-form">
						<div class="input-group">
							<div class="field-label">Organization Name</div>
							<v-text-field
								v-model="newOrganizationName"
								placeholder="Enter a name for your organization"
								:rules="[rules.required, rules.minLength]"
								:disabled="creatingOrganization"
								data-test="organization-name-input"
							/>
						</div>
					</div>

					<v-alert
						v-if="createOrgError"
						type="error"
						closable
						@click:close="createOrgError = ''"
					>
						{{ createOrgError }}
					</v-alert>

					<v-btn
						variant="flat"
						color="primary"
						type="submit"
						:loading="creatingOrganization"
						:disabled="!createOrgFormValid || creatingOrganization"
						data-test="create-organization-button"
					>
						<span class="icon-domain-plus" />
						Create Organization
					</v-btn>
				</v-form>
			</section>
		</div>

		<!-- User is not Enterprise tier -->
		<div
			v-else
			class="content-sections"
		>
			<section class="stack">
				<h4 class="sgnl-h6-semibold sgnl-text-color-default">Organizations</h4>
				<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
					Organizations allow you to manage team members, share resources, and collaborate on projects. This
					feature is available for Enterprise tier accounts.
				</div>
				<div class="sgnl-flex sgnl-flex-column sgnl-gap-8">
					<div class="sgnl-paragraph-l-medium sgnl-text-color-default">Benefits:</div>
					<ul class="ul-list sgnl-paragraph-s-regular sgnl-text-color-secondary">
						<li><DotMark16 class="ul-icon" />Centralized team management</li>
						<li><DotMark16 class="ul-icon" />Role-based access control</li>
						<li><DotMark16 class="ul-icon" />Shared resource pools</li>
						<li><DotMark16 class="ul-icon" />Enhanced collaboration features</li>
					</ul>
				</div>
				<v-btn
					variant="flat"
					color="primary"
					:to="{ name: 'Billing' }"
				>
					<Purchase16 />
					<span>Upgrade to Enterprise</span>
				</v-btn>
			</section>
		</div>

		<InviteMember
			v-if="showInviteDialog"
			v-model:show-dialog="showInviteDialog"
			@memberInvited="handleMemberInvited"
		/>

		<!-- Success Snackbar -->
		<v-snackbar
			v-model="successSnackbar"
			:timeout="4000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
		</v-snackbar>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import Add16 from "@carbon/icons-vue/es/add/16";
import Purchase16 from "@carbon/icons-vue/es/purchase/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import DotMark16 from "@carbon/icons-vue/es/dot-mark/16";

// Types
import { AWSAuthenticatedUserObject } from "@/types/user";
import { OrganizationDetails, OrganizationRole } from "@/types/api/organizations";

// Libraries
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import axios from "axios";

// Components
import ListTeamMembers from "@/components/Settings/Organization/ListTeamMembers.vue";
import InviteMember from "@/components/Settings/Organization/InviteMember.vue";
import OrganizationResourceUsage from "@/components/Settings/Organization/OrganizationResourceUsage.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";

// Stores
import { useUserStore } from "@/stores/user";

type ComponentData = {
	authenticatedUser: undefined | null | AWSAuthenticatedUserObject;
	currentTab: number;
	showInviteDialog: boolean;
	successSnackbar: boolean;
	successMessage: string;
	hasOrganization: boolean;
	isEnterpriseTier: boolean;
	organizationDetails: OrganizationDetails | null;
	resourceUsageError: string;
	currentUserRole: OrganizationRole | null;
	roleResolved: boolean;
	newOrganizationName: string;
	createOrgFormValid: boolean;
	creatingOrganization: boolean;
	createOrgError: string;
	rules: {
		required: (v: string) => boolean | string;
		minLength: (v: string) => boolean | string;
	};
};

export default defineComponent({
	name: "SettingsOrganizations",
	props: {},
	components: {
		ListTeamMembers,
		InviteMember,
		OrganizationResourceUsage,
		SkeletonBlock,
		Add16,
		Purchase16,
		CheckmarkFilled16,
		DotMark16,
	},
	data: (): ComponentData => ({
		authenticatedUser: undefined,
		currentTab: 0,
		showInviteDialog: false,
		successSnackbar: false,
		successMessage: "",
		hasOrganization: false,
		isEnterpriseTier: false,
		organizationDetails: null,
		resourceUsageError: "",
		currentUserRole: null,
		roleResolved: false,
		newOrganizationName: "",
		createOrgFormValid: false,
		creatingOrganization: false,
		createOrgError: "",
		rules: {
			required: (v: string) => !!v || "This field is required",
			minLength: (v: string) => (v && v.length >= 3) || "Must be at least 3 characters",
		},
	}),
	computed: {
		isOwner(): boolean {
			return this.currentUserRole === "Owner";
		},
	},
	setup() {
		const userStore = useUserStore();
		const listTeamMembersComponent = ref<InstanceType<typeof ListTeamMembers>>();
		return { userStore, listTeamMembersComponent };
	},
	methods: {
		async fetchOrganizationDetails(organizationId: string) {
			try {
				const response = await sdk.organizations.get(organizationId);
				this.organizationDetails = response;
				this.resourceUsageError = "";
			} catch (error) {
				let errorMessage = "Failed to load organization resource usage.";

				if (error instanceof SdkError) {
					switch (error.code) {
						case ERROR_CODES.API_NOT_FOUND:
							errorMessage = "Organization not found.";
							break;
						case ERROR_CODES.API_FORBIDDEN:
							errorMessage = "You don't have permission to view this organization.";
							break;
						case ERROR_CODES.API_SERVER_ERROR:
							errorMessage = "Server error loading resource usage.";
							break;
					}
				}

				this.resourceUsageError = errorMessage;
				console.error("Error fetching organization details:", error);
			}
		},
		async handleCreateOrganization() {
			// Validate form
			const form = this.$refs.createOrgForm as any;
			if (!form.validate()) {
				return;
			}

			this.creatingOrganization = true;
			this.createOrgError = "";

			try {
				const response = await this.userStore.createOrganization({
					Name: this.newOrganizationName,
				});
				// Show success message
				this.successMessage = `Organization "${response.Name}" created successfully!`;
				this.successSnackbar = true;

				// Refresh user data to get the new organization ID
				await this.userStore.fetchCurrentUserFromAWS();
				await this.userStore.fetchCurrentUserCustomizationsFromDB();

				// Get the organization ID and fetch details
				const organizationId = await this.userStore.getCurrentUserOrganizationID();
				if (organizationId) {
					this.hasOrganization = true;
					await this.fetchOrganizationDetails(organizationId);
				}

				// Reset form
				this.newOrganizationName = "";
				form.reset();
			} catch (error) {
				console.error("Error creating organization:", error);

				let errorMessage = "Failed to create organization. Please try again.";

				if (error instanceof SdkError) {
					switch (error.code) {
						case ERROR_CODES.API_BAD_REQUEST:
							errorMessage =
								(error.details as { Message?: string } | undefined)?.Message ||
								error.message ||
								"Invalid organization data.";
							break;
						case ERROR_CODES.API_FORBIDDEN:
							errorMessage = "You don't have permission to create an organization.";
							break;
						case ERROR_CODES.API_CONFLICT:
							errorMessage = "You already belong to an organization.";
							break;
						case ERROR_CODES.API_SERVER_ERROR:
							errorMessage = "Server error creating organization. Please contact support.";
							break;
					}
				} else if (axios.isAxiosError(error)) {
					// SDK forwards raw axios errors only when there's no response (network failure).
					errorMessage =
						"Failed to create the organization. Make sure you have an active internet connection.";
				}

				this.createOrgError = errorMessage;
			} finally {
				this.creatingOrganization = false;
			}
		},
		handleMemberInvited(data: { email: string; role: string; message: string }) {
			this.listTeamMembersComponent?.refreshList();

			// Show success message
			this.successMessage = data.message;
			this.successSnackbar = true;
		},
		handleRoleUpdated(role: OrganizationRole | null) {
			this.currentUserRole = role;
			this.roleResolved = true;
		},
	},
	async created() {
		const authUser = await this.userStore.getCurrentAuthenticatedUserObject();
		this.authenticatedUser = authUser;

		// Check if user is on Enterprise tier
		const userSubscriptions = await this.userStore.getCurrentUserSubscriptions();
		this.isEnterpriseTier =
			userSubscriptions?.Tier === "EnterpriseTierS" ||
			userSubscriptions?.Tier === "EnterpriseTierM" ||
			userSubscriptions?.Tier === "EnterpriseTierL";

		// Check if user has an organization
		const organizationId = await this.userStore.getCurrentUserOrganizationID();
		this.hasOrganization = organizationId !== null;

		// Fetch organization details if user has an organization
		if (organizationId) {
			await this.fetchOrganizationDetails(organizationId);
		}
	},
});
</script>

<style lang="scss" scoped>
/* Reserve the invite-button height so the row stays put whether the button,
   its loading skeleton, or nothing (non-owners) is shown. */
.members-header {
	min-height: var(--sgnl-unit-36);
}

.create-org-form,
.org-settings-form {
	max-width: var(--sgnl-container-343);
}

.org-settings-form :deep(.v-field--disabled) {
	opacity: 1;
	border-color: var(--sgnl-default-border-disabled);
	color: var(--sgnl-default-text-disabled);
}

.org-settings-form :deep(.v-field--disabled .v-field__outline) {
	--v-field-border-opacity: 1;
	color: var(--sgnl-default-border-disabled);
}

.org-settings-form :deep(.v-field--disabled input) {
	color: var(--sgnl-default-text-disabled);
}

.support-link {
	text-decoration: underline;
}

:deep(.v-window) {
	overflow: visible;
}

:deep(.v-window__container) {
	overflow: visible;
}

:deep(.v-window-item) {
	overflow: visible;
}

.icon-domain-plus {
	display: inline-block;
	width: 16px;
	height: 16px;
	background-color: currentColor;
	mask-image: url(@/assets/icons/settings/domain-plus-16.svg);
	mask-size: contain;
	mask-repeat: no-repeat;
	mask-position: center;
}
</style>
